/**
 * Payment Service for Payment Processing and Method Management
 * 
 * This service handles:
 * - Payment method management (CRUD operations)
 * - Payment processing (charge, refund, etc.)
 * - Transaction history
 * - Payment provider integration points
 */

import apiService from './api';

/**
 * Get all payment methods for the current user
 * @returns {Promise<Array>} - Array of payment methods
 */
export async function getPaymentMethods() {
  try {
    const paymentMethods = await apiService.getPaymentMethods();
    
    // Handle paginated response from DRF
    if (paymentMethods.results) {
      return paymentMethods.results;
    }
    
    // If not paginated, return the data directly
    return Array.isArray(paymentMethods) ? paymentMethods : [];
  } catch (error) {
    console.error('Get payment methods error:', error);
    throw error;
  }
}

/**
 * Create a new payment method
 * @param {Object} paymentMethodData - Payment method data
 * @returns {Promise<Object>} - Created payment method
 */
export async function createPaymentMethod(paymentMethodData) {
  try {
    return await apiService.createPaymentMethod(paymentMethodData);
  } catch (error) {
    console.error('Create payment method error:', error);
    throw error;
  }
}

/**
 * Update a payment method
 * @param {number} paymentMethodId - Payment method ID
 * @param {Object} paymentMethodData - Updated payment method data
 * @returns {Promise<Object>} - Updated payment method
 */
export async function updatePaymentMethod(paymentMethodId, paymentMethodData) {
  try {
    return await apiService.updatePaymentMethod(paymentMethodId, paymentMethodData);
  } catch (error) {
    console.error('Update payment method error:', error);
    throw error;
  }
}

/**
 * Delete a payment method
 * @param {number} paymentMethodId - Payment method ID
 * @returns {Promise<Object>} - Success response
 */
export async function deletePaymentMethod(paymentMethodId) {
  try {
    return await apiService.deletePaymentMethod(paymentMethodId);
  } catch (error) {
    console.error('Delete payment method error:', error);
    throw error;
  }
}

/**
 * Set a payment method as default
 * @param {number} paymentMethodId - Payment method ID
 * @returns {Promise<Object>} - Success response
 */
export async function setDefaultPaymentMethod(paymentMethodId) {
  try {
    return await apiService.setDefaultPaymentMethod(paymentMethodId);
  } catch (error) {
    console.error('Set default payment method error:', error);
    throw error;
  }
}

/**
 * Process a payment
 * @param {Object} paymentData - Payment data
 * @param {number} paymentData.payment_method_id - Payment method ID to charge
 * @param {number} paymentData.amount - Amount in cents
 * @param {number} paymentData.booking_id - Associated booking ID
 * @param {string} paymentData.description - Payment description
 * @param {Object} paymentData.metadata - Additional metadata
 * @returns {Promise<Object>} - Payment result with transaction details
 */
export async function processPayment(paymentData) {
  try {
    const result = await apiService.processPayment(paymentData);
    return result;
  } catch (error) {
    console.error('Process payment error:', error);
    throw error;
  }
}

/**
 * Get payment/transaction history
 * @param {Object} filters - Optional filters (status, date_from, date_to, etc.)
 * @returns {Promise<Array>} - Array of transactions
 */
export async function getTransactions(filters = {}) {
  try {
    const transactions = await apiService.getTransactions(filters);
    
    if (transactions.results) {
      return transactions.results;
    }
    
    return Array.isArray(transactions) ? transactions : [];
  } catch (error) {
    console.error('Get transactions error:', error);
    throw error;
  }
}

/**
 * Get a specific transaction by ID
 * @param {number} transactionId - Transaction ID
 * @returns {Promise<Object>} - Transaction details
 */
export async function getTransaction(transactionId) {
  try {
    return await apiService.getTransaction(transactionId);
  } catch (error) {
    console.error('Get transaction error:', error);
    throw error;
  }
}

/**
 * Request a refund for a transaction
 * @param {number} transactionId - Transaction ID to refund
 * @param {number} amount - Amount to refund (optional, defaults to full amount)
 * @param {string} reason - Refund reason
 * @returns {Promise<Object>} - Refund result
 */
export async function requestRefund(transactionId, amount = null, reason = '') {
  try {
    return await apiService.requestRefund(transactionId, { amount, reason });
  } catch (error) {
    console.error('Request refund error:', error);
    throw error;
  }
}

/**
 * Create a charge for Stitch payment processing
 * Step 1 of the Stitch payment flow
 * @param {Object} chargeData - Charge data
 * @param {number} chargeData.booking - Booking ID
 * @param {string} chargeData.amount - Amount in ZAR (e.g., "250.00")
 * @param {string} chargeData.description - Charge description
 * @returns {Promise<Object>} - Created charge with ID
 */
export async function createCharge(chargeData) {
  try {
    const response = await apiService.request('/billing/charges/', {
      method: 'POST',
      body: JSON.stringify(chargeData)
    });
    return response;
  } catch (error) {
    console.error('Create charge error:', error);
    throw error;
  }
}

/**
 * Create a payment intent to get Stitch payment URL
 * Step 2 of the Stitch payment flow
 *
 * Stitch needs somewhere to send the user back to once they've paid or
 * bailed, so both URLs are always sent.
 *
 * @param {number} chargeId - Charge ID from createCharge
 * @param {Object} [options] - Optional redirect overrides
 * @param {string} [options.returnUrl] - Where Stitch sends the user after payment
 * @param {string} [options.cancelUrl] - Where Stitch sends the user on cancel
 * @returns {Promise<Object>} - Payment intent with payment_url
 */
export async function createPaymentIntent(chargeId, options = {}) {
  try {
    const origin = window.location.origin;
    const response = await apiService.request('/billing/payment-intents/', {
      method: 'POST',
      body: JSON.stringify({
        charge_id: chargeId,
        return_url: options.returnUrl || `${origin}/payment/return`,
        cancel_url: options.cancelUrl || `${origin}/payment/return?cancelled=1`
      })
    });
    return response;
  } catch (error) {
    console.error('Create payment intent error:', error);
    throw error;
  }
}

/**
 * Verify payment status after redirect from Stitch
 * @param {number} chargeId - Charge ID to verify
 * @returns {Promise<Object>} - Payment status (paid, pending, cancelled, failed)
 */
export async function verifyPaymentStatus(chargeId) {
  try {
    const response = await apiService.request(`/billing/payments/verify/${chargeId}/`, {
      method: 'GET'
    });
    return response;
  } catch (error) {
    console.error('Verify payment error:', error);
    throw error;
  }
}

/**
 * Verify card with a small authorization (for card verification)
 * @param {number} paymentMethodId - Payment method ID to verify
 * @returns {Promise<Object>} - Verification result
 */
export async function verifyPaymentMethod(paymentMethodId) {
  try {
    return await apiService.verifyPaymentMethod(paymentMethodId);
  } catch (error) {
    console.error('Verify payment method error:', error);
    throw error;
  }
} 